import React, { useEffect, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatJMD } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Copy, Upload, FileCheck2, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export default function OrderConfirmation() {
  const { state } = useLocation();
  const order = state?.order;
  const [bank, setBank] = useState(null);
  const [uploaded, setUploaded] = useState(!!order?.payment_proof_path);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/bank-details").then((r) => setBank(r.data)).catch(() => {});
  }, []);

  if (!order) {
    return (
      <div className="pt-32 pb-24 text-center">
        <p className="font-display text-4xl">NO ORDER FOUND</p>
        <Link to="/shop" className="font-mono text-xs uppercase text-jamaica-gold mt-4 inline-block">Back to shop</Link>
      </div>
    );
  }

  const ref = order.order_reference || order.order_number;
  const contact = (order.customer?.phone || order.customer?.email || "").trim();
  const isManual = order.order_status !== "Payment Confirmed";
  const paymentLabel = (order.payment_method || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const copy = () => { navigator.clipboard.writeText(ref); toast.success("Order reference copied"); };
  const copyAcct = () => { navigator.clipboard.writeText(bank.account_number); toast.success("Account number copied"); };
  const waText = `Hi Turahtid! Here is my payment receipt for order ${ref} (${formatJMD(order.total_cents)}). [Attach receipt photo]`;
  const waHref = `https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}?text=${encodeURIComponent(waText)}`;
  const trackHref = `/track?ref=${encodeURIComponent(ref)}`;

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("contact", contact);
    fd.append("file", file);
    try {
      await api.post(`/orders/${encodeURIComponent(ref)}/payment-proof`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploaded(true);
      toast.success("Receipt received — we'll confirm your payment shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed. Try again.");
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div data-testid="confirmation-page" className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16">
      <div className="text-center">
        <CheckCircle2 className="w-14 h-14 text-jamaica-green mx-auto mb-4" />
        <h1 data-testid="confirmation-heading" className="font-display text-3xl sm:text-4xl tracking-tight leading-tight">RESPECT! TO COMPLETE YOUR ORDER SEND PAYMENT TO SAGICOR ACCOUNT LISTED BELOW AND UPLOAD PROOF OF PAYMENT</h1>
        <p className="text-zinc-400 mt-2 text-sm">Big up yuhself. We received your order and a-go sort it fi yuh.</p>
      </div>

      <div className="border border-jamaica-gold/40 bg-card p-5 mt-8">
        <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Order Reference</p>
        <div className="flex items-center gap-3 mt-1">
          <span data-testid="order-number" className="font-display text-3xl text-jamaica-gold tracking-wider">{ref}</span>
          <button onClick={copy} data-testid="copy-order-number" className="text-zinc-400 hover:text-jamaica-gold"><Copy className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-zinc-500 mt-2">Save this to track your order anytime. Orders are confirmed once payment is received; delivery details and tracking are sent after payment and dispatch.</p>
      </div>

      {isManual && (
      <div data-testid="bank-details" className="border border-white/10 bg-card p-5 mt-4">
        <p className="font-heading font-bold uppercase text-sm mb-2">Bank Transfer Details</p>
        <p className="text-sm text-zinc-400">Send <span className="text-jamaica-gold font-mono">{formatJMD(order.total_cents)}</span> to:</p>
        <ul className="text-sm text-zinc-300 font-mono mt-2 space-y-1">
          <li>{bank?.bank_name || "Sagicor Bank Jamaica"} · {bank?.account_name || "Turahtid Clothing Ltd"}</li>
          {bank?.account_type && <li>Type: {bank.account_type}</li>}
          <li className="flex items-center gap-2">
            <span data-testid="bank-account-number">Acct: {bank?.account_number || "—"}</span>
            {bank?.account_number && (
              <button onClick={copyAcct} data-testid="copy-account-number" className="inline-flex items-center gap-1 border border-jamaica-gold/50 px-2 py-1 text-[10px] uppercase tracking-widest text-jamaica-gold hover:bg-jamaica-gold hover:text-black transition-colors">
                <Copy className="w-3 h-3" /> Copy
              </button>
            )}
          </li>
          {bank?.branch && <li>Branch: {bank.branch}</li>}
          <li className="text-jamaica-gold">Ref: {ref}</li>
        </ul>
        <p className="text-[11px] text-zinc-500 mt-2">Use your order reference as the transfer note so we can match your payment.</p>
      </div>
      )}

      {isManual && (
      <div data-testid="proof-upload-section" className="border border-jamaica-green/40 bg-card p-5 mt-4">
        <p className="font-heading font-bold uppercase text-sm mb-2">Upload Payment Receipt</p>
        <p className="text-xs text-zinc-500 mb-3">Send us your bank transfer receipt so we can confirm your payment fast. JPG, PNG, WEBP or PDF · max 10MB.</p>
        <input ref={fileRef} data-testid="proof-file-input" type="file" accept="image/*,application/pdf" onChange={onFile} className="hidden" />
        {uploaded ? (
          <div data-testid="proof-uploaded" className="flex items-center gap-2 text-jamaica-green font-mono text-sm">
            <FileCheck2 className="w-5 h-5" /> Receipt received. We'll confirm shortly.
          </div>
        ) : (
          <Button
            data-testid="upload-proof-btn"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="bg-jamaica-green hover:bg-green-700 text-white font-bold uppercase tracking-widest rounded-none"
          >
            <Upload className="w-4 h-4 mr-2" /> {uploading ? "Uploading…" : "Upload Receipt"}
          </Button>
        )}
        <a
          href={waHref}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="whatsapp-receipt-btn"
          className="mt-3 flex w-full sm:w-auto sm:inline-flex items-center justify-center gap-2 border border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-black px-4 py-2 font-bold uppercase tracking-widest text-xs transition-colors sm:ml-3"
        >
          <MessageCircle className="w-4 h-4" /> Send Receipt on WhatsApp
        </a>
      </div>
      )}

      <div className="border border-white/10 bg-card p-5 mt-4">
        <p className="font-heading font-bold uppercase text-sm mb-3">Summary</p>
        {order.items.map((i, idx) => (
          <div key={idx} className="flex justify-between text-sm py-1">
            <span className="text-zinc-300">{i.title} <span className="text-zinc-500 font-mono text-xs">({i.size} ×{i.qty})</span></span>
            <span className="font-mono text-zinc-400">{formatJMD(i.price_cents * i.qty)}</span>
          </div>
        ))}
        <div className="border-t border-white/10 mt-3 pt-3 space-y-1 font-mono text-sm">
          <div className="flex justify-between"><span className="text-zinc-400 text-xs uppercase">Subtotal</span><span>{formatJMD(order.subtotal_cents)}</span></div>
          {order.discount_cents > 0 && <div className="flex justify-between text-jamaica-green"><span className="text-xs uppercase" data-testid="confirm-discount-label">Sale Discount</span><span data-testid="confirm-discount">−{formatJMD(order.discount_cents)}</span></div>}
          <div className="flex justify-between"><span className="text-zinc-400 text-xs uppercase">Delivery</span><span>{order.delivery_cents === 0 ? "FREE" : formatJMD(order.delivery_cents)}</span></div>
          <div className="flex justify-between text-base"><span className="text-xs uppercase self-center">Total</span><span className="text-jamaica-gold">{formatJMD(order.total_cents)}</span></div>
        </div>
        <p className="text-xs text-zinc-500 mt-3">Payment: {paymentLabel}</p>
      </div>

      <div className="flex gap-3 mt-6">
        <Link to={trackHref} className="flex-1">
          <Button data-testid="go-track-btn" className="w-full py-6 bg-jamaica-gold hover:bg-yellow-300 text-black font-bold uppercase tracking-widest rounded-none">Track My Order</Button>
        </Link>
        <Link to="/shop" className="flex-1">
          <Button variant="outline" className="w-full py-6 border-border text-white hover:bg-secondary rounded-none uppercase tracking-widest font-bold">Keep Shopping</Button>
        </Link>
      </div>
    </div>
  );
}
